import { getIpStatus } from "../Enums/ipPlanEnums";

// F-07 — IP Completeness Score
//
// Pure helper that computes a completeness percentage for an IP Plan card from
// data already available on the platform. It is intentionally read-only and
// derives everything from existing fields — it does NOT change any card data,
// status, or logic.
//
// It accepts the RAW IP plan object as returned by either:
//   - /api/IPPlan/getAllIPPlans  (list items), or
//   - /api/IPPlan/getIPPlan?id=  (single card detail)
// Both shapes expose the same nested fields used below, so the score is
// consistent across the board column, the card preview, and the home panel.
//
// NOTE (known limitation): the platform improvement plan's fuller formula also
// factors linked site visits, tracking, submitted reports and flag resolution.
// Those counts are not present on the IP plan responses today, so they are not
// included here. The component list below is structured so they can be added
// without changing call sites once the backend exposes per-IP counts.

const hasText = (value) =>
  typeof value === "string" && value.trim().length > 0 && value !== "N/A";

const hasDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
};

// Map the IP status (1–5) to a higher-level monitoring band for display.
export const getIpCompletenessBand = (currentIPStatus) => {
  switch (Number(currentIPStatus)) {
    case 1:
    case 2:
    case 3:
      return "Planning";
    case 4:
      return "Implementation";
    case 5:
      return "Closed";
    default:
      return "Not started";
  }
};

/**
 * @param {object} ipItem - raw IP plan object
 * @returns {{
 *   percent: number,
 *   completeCount: number,
 *   totalCount: number,
 *   band: string,
 *   status: { text: string, fill: string },
 *   components: Array<{ label: string, complete: boolean }>
 * }}
 */
export const getIpCompleteness = (ipItem) => {
  const item = ipItem || {};

  const assigneeName =
    item?.assignee?.userDetails?.firstName ||
    item?.assignee?.userName ||
    item?.assignee?.email;

  const components = [
    {
      label: "Implementing partner",
      complete: hasText(item?.implementingPartner?.name),
    },
    { label: "Field coordinator assigned", complete: !!assigneeName },
    {
      label: "Location (governorate/state)",
      complete: Array.isArray(item?.ipPlanStates) && item.ipPlanStates.length > 0,
    },
    {
      label: "Clusters",
      complete: Array.isArray(item?.clusters) && item.clusters.length > 0,
    },
    { label: "Point of contact name", complete: hasText(item?.pointOfContact?.name) },
    { label: "Point of contact email", complete: hasText(item?.pointOfContact?.email) },
    { label: "On-site operations info", complete: hasText(item?.generalInfo) },
    {
      label: "Program activities description",
      complete: hasText(item?.planningRound?.programDescription),
    },
    { label: "Initial meeting date", complete: hasDate(item?.initialMeeting) },
    {
      label: "IP status set",
      complete: Number(item?.currentIPStatus) >= 1 && Number(item?.currentIPStatus) <= 5,
    },
  ];

  const totalCount = components.length;
  const completeCount = components.filter((c) => c.complete).length;
  const percent = totalCount === 0 ? 0 : Math.round((completeCount / totalCount) * 100);

  const status = getIpStatus(item?.currentIPStatus);

  return {
    percent,
    completeCount,
    totalCount,
    band: getIpCompletenessBand(item?.currentIPStatus),
    status: { text: status?.text, fill: status?.fill },
    components,
  };
};

// Backend stageBand (F-07 getIPCompleteness / getProjectIPCompleteness):
// 1=Planning | 2=Implementation | 3=Reporting | 4=Closed.
export const stageBandLabel = (stageBand) => {
  switch (Number(stageBand)) {
    case 1:
      return "Planning";
    case 2:
      return "Implementation";
    case 3:
      return "Reporting";
    case 4:
      return "Closed";
    default:
      return "Not started";
  }
};

// Colour the progress bar by how complete the card is (brand magenta when done).
export const getCompletenessColor = (percent) => {
  if (percent >= 80) return "#AA1A5F";
  if (percent >= 40) return "#FC9403";
  return "#D4380D";
};
